/**
 * Store locator: the clear (X) button brings back the full list.
 *
 * Stockist's X only empties the field and leaves the last results on screen, and it has no
 * setting or public API for this. The widget does expose window.__stockist_reload, which builds
 * a widget on a <stockist-store-locator> that has none yet, so a clear swaps the element for a
 * fresh copy and lets Stockist start over exactly as on page load.
 *
 * The widget renders into a closed shadow root, reachable only through its own input. We bind to
 * that root on the first focus inside the widget: the X only appears after typing, so focus
 * always comes first, and the async vendor script has long finished by then.
 */
(() => {
  const HOST_SELECTOR = "stockist-store-locator";
  const CLEAR_SELECTOR = ".stockist-clear-search-button";
  // The new widget builds its form only after fetching its config, so focus waits for it.
  const INPUT_POLL_INTERVAL_MS = 100;
  const INPUT_POLL_ATTEMPTS = 30;
  const boundRoots = new WeakSet();
  let isResetting = false;
  let warnedMissingInternals = false;
  let missingInputNoted = false;

  function getWidgetInput() {
    return window.Stockist?.__widget?._form?._input || null;
  }

  function warnMissingInternals() {
    if (warnedMissingInternals) return;
    warnedMissingInternals = true;
    console.warn(
      "Store locator: Stockist internals are missing, so clear will not reset the results."
    );
  }

  // loaded is set before __widget exists, so a missing input on the first focus is usually
  // still the config fetch. Warn only if the path is still gone after that wait.
  function noteMissingInput() {
    if (warnedMissingInternals || missingInputNoted) return;
    missingInputNoted = true;
    window.setTimeout(() => {
      if (!getWidgetInput()) warnMissingInternals();
    }, INPUT_POLL_INTERVAL_MS * INPUT_POLL_ATTEMPTS);
  }

  async function waitForWidgetInput() {
    for (let attempt = 0; attempt < INPUT_POLL_ATTEMPTS; attempt += 1) {
      const input = getWidgetInput();
      if (input?.isConnected) return input;
      await new Promise((resolve) => setTimeout(resolve, INPUT_POLL_INTERVAL_MS));
    }
    return null;
  }

  async function resetWidget(host) {
    if (isResetting) return;
    if (typeof window.__stockist_reload !== "function") {
      warnMissingInternals();
      return;
    }
    isResetting = true;
    let freshHost = null;

    try {
      freshHost = host.cloneNode(false);
      host.replaceWith(freshHost);
      await window.__stockist_reload();
      const nextInput = await waitForWidgetInput();
      if (!nextInput) {
        warnMissingInternals();
        // Resolved without a form. Put the previous widget back instead of leaving the clone.
        if (freshHost?.isConnected) freshHost.replaceWith(host);
        return;
      }
      // Reload finished. A focus failure must not swap the working widget back out.
      freshHost = null;
      // The old input left the page with its host, so keep the shopper in the search field.
      nextInput.focus();
    } catch (error) {
      console.warn("Store locator: could not reset the search.", error);
      if (freshHost?.isConnected) freshHost.replaceWith(host);
    } finally {
      isResetting = false;
    }
  }

  function bindRoot() {
    const input = getWidgetInput();
    if (!input) {
      noteMissingInput();
      return;
    }
    const root = input.getRootNode();
    if (!root || root === document || boundRoots.has(root)) return;

    boundRoots.add(root);
    root.addEventListener("click", (event) => {
      if (!event.target.closest?.(CLEAR_SELECTOR)) return;
      resetWidget(root.host);
    });
  }

  document.addEventListener("focusin", (event) => {
    if (event.target.matches?.(HOST_SELECTOR)) bindRoot();
  });
})();
